import React, { useCallback, useEffect, useState } from 'react';
import { ChoiceRadio } from '../../../../components/common/BooleanRadio/BooleanRadio';
import Checkbox from '../../../../components/common/Checkbox/Checkbox';
import Icon from '../../../../components/common/Icon/Icon';
import Loading from '../../../../components/common/Loading/Loading';
import { StudentService } from '../../../../services';
import {
  STUDENT_BEHAVIORAL_PILLAR_LABELS,
  STUDENT_BEHAVIORAL_PILLAR_POINTS,
  STUDENT_BEHAVIORAL_PILLARS,
  STUDENT_BEHAVIORAL_TAG_APPROACH,
  STUDENT_BEHAVIORAL_TAG_GUIDE_ORDER,
  STUDENT_BEHAVIORAL_TAG_LABELS,
  STUDENT_BEHAVIORAL_TAG_TONE,
  STUDENT_EXIT_REVERSIBLE_LABELS,
  STUDENT_EXIT_REVERSIBLE_OPTIONS,
  STUDENT_EXIT_RISK_LABELS,
  STUDENT_EXIT_RISK_OPTIONS,
  emptyStudentBehavioralPillars,
  studentBehavioralProfileScore,
  type StudentBehavioralPillar,
  type StudentBehavioralPillars,
  type StudentBehavioralTag,
  type StudentBehavioralTagTone,
  type StudentExitReversible,
  type StudentExitRisk,
} from '../../../../types/studentBehavioralProfile';
import styles from './BehavioralProfileStep.module.css';

interface BehavioralProfileStepProps {
  studentId: number;
}

const EXIT_RISK_CHOICES = STUDENT_EXIT_RISK_OPTIONS.map((option) => ({
  value: option,
  label: STUDENT_EXIT_RISK_LABELS[option],
}));

const EXIT_REVERSIBLE_CHOICES = STUDENT_EXIT_REVERSIBLE_OPTIONS.map((option) => ({
  value: option,
  label: STUDENT_EXIT_REVERSIBLE_LABELS[option],
}));

const toneSurfaceClass = (tone: StudentBehavioralTagTone): string => styles[`tone-${tone}`] ?? '';

const scoreValueClass = (score: number): string => {
  if (score < 20) return styles.scoreValue0;
  if (score < 40) return styles.scoreValue20;
  if (score < 60) return styles.scoreValue40;
  if (score < 80) return styles.scoreValue60;
  return styles.scoreValue80;
};

const tagColorMarkClass = (tone: StudentBehavioralTagTone): string => styles[`tagColorMark-${tone}`] ?? '';

const BehavioralProfileStep: React.FC<BehavioralProfileStepProps> = ({ studentId }) => {
  const [tags, setTags] = useState<StudentBehavioralTag[]>([]);
  const [pillars, setPillars] = useState<StudentBehavioralPillars>(emptyStudentBehavioralPillars);
  const [exitRisk, setExitRisk] = useState<StudentExitRisk | null>(null);
  const [reversible, setReversible] = useState<StudentExitReversible | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [guideExpanded, setGuideExpanded] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const profile = await StudentService.getBehavioralProfile(studentId);
      setTags(profile.tags);
      setPillars(profile.pillars);
      setExitRisk(profile.exitRisk);
      setReversible(profile.reversible);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Não foi possível carregar o perfil comportamental.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const toggleTag = (tag: StudentBehavioralTag) => {
    setSavedMessage(null);
    setTags((current) => (
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag]
    ));
  };

  const togglePillar = (pillar: StudentBehavioralPillar) => {
    setSavedMessage(null);
    setPillars((current) => ({
      ...current,
      [pillar]: !current[pillar],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const profile = await StudentService.saveBehavioralProfile(studentId, {
        tags,
        pillars,
        exitRisk,
        reversible,
      });
      setTags(profile.tags);
      setPillars(profile.pillars);
      setExitRisk(profile.exitRisk);
      setReversible(profile.reversible);
      setSavedMessage('Perfil comportamental salvo.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Não foi possível salvar o perfil comportamental.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  const score = studentBehavioralProfileScore(pillars);

  return (
    <div className={styles.panel}>
      <p className={styles.score}>
        <span className={styles.scoreLabel}>Score comportamental</span>
        <span className={styles.scoreValueWrap}>
          <strong className={`${styles.scoreValue} ${scoreValueClass(score)}`}>{score}</strong>
          <span className={styles.scoreTotal}>/100</span>
        </span>
      </p>

      <div className={styles.classificationRow}>
        <fieldset className={styles.choiceField}>
          <legend className={styles.choiceLegend}>Risco de saída da consultoria</legend>
          <ChoiceRadio
            name="student-exit-risk"
            value={exitRisk}
            options={EXIT_RISK_CHOICES}
            disabled={saving}
            onChange={(option) => {
              setSavedMessage(null);
              setExitRisk(option);
            }}
          />
        </fieldset>

        <fieldset className={styles.choiceField}>
          <legend className={styles.choiceLegend}>Reversível (convencer a não sair)</legend>
          <ChoiceRadio
            name="student-exit-reversible"
            value={reversible}
            options={EXIT_REVERSIBLE_CHOICES}
            disabled={saving}
            onChange={(option) => {
              setSavedMessage(null);
              setReversible(option);
            }}
          />
        </fieldset>
      </div>

      <div className={styles.mainLayout}>
        <div className={styles.mainColumn}>
          <section className={styles.section}>
            <div className={styles.guideHeader}>
              <div className={styles.guideHeaderText}>
                <h3 className={styles.sectionTitle}>Guia de abordagem</h3>
              </div>
              <button
                type="button"
                className={styles.guideToggle}
                aria-expanded={guideExpanded}
                onClick={() => setGuideExpanded((current) => !current)}
              >
                <Icon
                  name="expand_more"
                  size={20}
                  className={guideExpanded ? styles.guideToggleIconExpanded : ''}
                />
                {guideExpanded ? 'Ocultar guia' : 'Ver guia'}
              </button>
            </div>
            {guideExpanded && (
              <ul className={styles.guideList}>
                {STUDENT_BEHAVIORAL_TAG_GUIDE_ORDER.map((tag) => {
                  const tone = STUDENT_BEHAVIORAL_TAG_TONE[tag];
                  return (
                    <li
                      key={tag}
                      className={[styles.guideItem, toneSurfaceClass(tone)].filter(Boolean).join(' ')}
                    >
                      <span className={styles.guideLabel}>{STUDENT_BEHAVIORAL_TAG_LABELS[tag]}</span>
                      <span className={styles.guideText}>{STUDENT_BEHAVIORAL_TAG_APPROACH[tag]}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Tags da aluna</h3>
            <div className={styles.tagList}>
              {STUDENT_BEHAVIORAL_TAG_GUIDE_ORDER.map((tag) => {
                const tone = STUDENT_BEHAVIORAL_TAG_TONE[tag];
                return (
                  <div key={tag} className={styles.tagRow}>
                    <span
                      className={[styles.tagColorMark, tagColorMarkClass(tone)].filter(Boolean).join(' ')}
                      aria-hidden="true"
                    />
                    <Checkbox
                      variant="classic"
                      checked={tags.includes(tag)}
                      disabled={saving}
                      label={STUDENT_BEHAVIORAL_TAG_LABELS[tag]}
                      className={styles.tagCheckbox}
                      onPress={() => toggleTag(tag)}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <aside className={styles.pillarsColumn}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Pilares</h3>
            <p className={styles.hint}>Cada pilar marcado vale {STUDENT_BEHAVIORAL_PILLAR_POINTS} pontos.</p>
            <div className={styles.pillarList}>
              {STUDENT_BEHAVIORAL_PILLARS.map((pillar) => (
                <Checkbox
                  key={pillar}
                  variant="classic"
                  checked={pillars[pillar]}
                  disabled={saving}
                  label={STUDENT_BEHAVIORAL_PILLAR_LABELS[pillar]}
                  onPress={() => togglePillar(pillar)}
                />
              ))}
            </div>
          </section>
        </aside>
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {savedMessage && <p className={styles.success}>{savedMessage}</p>}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.saveButton}
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Salvando...' : 'Salvar perfil'}
        </button>
      </div>
    </div>
  );
};

export default BehavioralProfileStep;
